'use client';

import { useState, ComponentProps } from 'react';
import { useMediaQuery } from 'react-responsive';
import get from 'lodash/fp/get';

import { usePathname } from 'next/navigation';
import { type FSLocale } from '@fshr/shared';
import { cn } from '@fshr/shared-react';

import Select from '../select';

interface SelectProps extends ComponentProps<typeof Select> {
	label: string;
	value: FSLocale;
	className?: string;
	options: Array<{ label: string; value: FSLocale }>;
}

export default function SelectWrapper({ label, value, options, className, ...props }: SelectProps) {
	const [isPending, setIsPending] = useState(false);
	const pathname = usePathname();
	const isMobile = useMediaQuery({ maxWidth: 700 });

	const regex = new RegExp(
		`^/(` +
			options
				.map(get('value'))
				.map(str => (str as string).replace(/[-_]/g, '[\\-_]?'))
				.join('|') +
			')/'
	);

	function handleSelectChange(event: React.ChangeEvent<HTMLSelectElement>) {
		setIsPending(true);
		const nextLocale = event.target.value as FSLocale;
		const urlLocale = nextLocale === 'en' ? '' : `/${nextLocale}`;
		window.location.href = urlLocale + pathname.replace(regex, `/`);
	}

	return (
		<Select
			aria-label={label}
			defaultValue={value}
			disabled={isPending}
			onChange={handleSelectChange}
			className={cn(
				['w-[111px]', 'lg:w-[100px]', 'ltr:after:right-0', 'after:border-t-inherit'],
				{
					'text-caption2': !isMobile,
					'text-caption3': isMobile,
					'cursor-not-allowed opacity-50': isPending,
					'hover:text-neutral-900 focus:text-neutral-900': !isPending,
				},
				className
			)}
			options={options}
			selectClassName="ltr:pl-0 rtl:pr-0"
			{...props}
		/>
	);
}
